import type { IconType } from 'react-icons';
import {
  MdDescription,
  MdRequestQuote,
  MdShoppingCart,
  MdReceiptLong,
  MdLocalShipping,
  MdGavel,
} from 'react-icons/md';

/** Canonical procurement workflow steps (API / stepper). */
export type ProcurementWorkflowStatus =
  | 'RFQ'
  | 'QUOTATION'
  | 'PURCHASE'
  | 'ORDERED'
  | 'DELIVERY'
  | 'CLAIM_MANAGEMENT';

export type StepVisualState = 'completed' | 'active' | 'pending';

export interface ProcurementWorkflowStepDefinition {
  id: ProcurementWorkflowStatus;
  title: string;
  shortTitle: string;
  description: string;
  icon: IconType;
}

export const PROCUREMENT_WORKFLOW_STEPS: ProcurementWorkflowStepDefinition[] = [
  {
    id: 'RFQ',
    title: 'RFQ',
    shortTitle: 'RFQ',
    description: 'Request for quotation issued to vendors',
    icon: MdDescription,
  },
  {
    id: 'QUOTATION',
    title: 'Quotation',
    shortTitle: 'Quote',
    description: 'Vendor quotations received and compared',
    icon: MdRequestQuote,
  },
  {
    id: 'PURCHASE',
    title: 'Purchase',
    shortTitle: 'Purchase',
    description: 'Award vendor and configure purchase',
    icon: MdShoppingCart,
  },
  {
    id: 'ORDERED',
    title: 'Ordered',
    shortTitle: 'Ordered',
    description: 'Purchase order issued and acknowledged',
    icon: MdReceiptLong,
  },
  {
    id: 'DELIVERY',
    title: 'Delivery',
    shortTitle: 'Delivery',
    description: 'Shipment tracking and goods receipt',
    icon: MdLocalShipping,
  },
  {
    id: 'CLAIM_MANAGEMENT',
    title: 'Claim Management',
    shortTitle: 'Claims',
    description: 'Post-delivery claims and dispute resolution',
    icon: MdGavel,
  },
];

const STATUS_ALIASES: Record<string, ProcurementWorkflowStatus> = {
  RFQ: 'RFQ',
  QUOTATION: 'QUOTATION',
  QUOTE: 'QUOTATION',
  QUOTED: 'QUOTATION',
  PURCHASE: 'PURCHASE',
  PURCHASING: 'PURCHASE',
  ORDERED: 'ORDERED',
  ORDER: 'ORDERED',
  FINALIZED: 'ORDERED',
  AWARDED: 'ORDERED',
  DELIVERY: 'DELIVERY',
  DELIVERED: 'DELIVERY',
  SHIPPED: 'DELIVERY',
  CLAIM_MANAGEMENT: 'CLAIM_MANAGEMENT',
  CLAIM: 'CLAIM_MANAGEMENT',
  CLAIMS: 'CLAIM_MANAGEMENT',
};

const CLAIM_ELIGIBLE_DELIVERY_STATUSES = new Set([
  'onboard_received',
  'delivery_complete',
  'received_by_warehouse',
]);

const DELIVERY_PO_STATUSES = new Set(['shipped', 'delivered', 'completed']);
const ORDERED_PO_STATUSES = new Set(['issued', 'acknowledged', 'in_progress']);
const DELIVERY_WORKFLOW_STATUSES = new Set([
  'assigned',
  'acknowledged',
  'in_transit',
  'with_port_agent',
  'out_for_delivery',
  'onboard_received',
  'received_by_warehouse',
  'delivery_complete',
]);
const SHIPMENT_STATUSES_IN_DELIVERY = new Set([
  'pending',
  'in-transit',
  'at-port',
  'on-vessel',
  'delivered',
  'delayed',
]);

/** Normalize API / RFQ status strings to a workflow step id. */
export function normalizeProcurementWorkflowStatus(
  raw?: string | null
): ProcurementWorkflowStatus | null {
  if (!raw || typeof raw !== 'string') return null;
  const key = raw.trim().toUpperCase().replace(/[\s-]+/g, '_');
  return STATUS_ALIASES[key] ?? null;
}

export interface ProcurementWorkflowContext {
  /** Explicit workflow status from API (preferred). */
  workflowStatus?: string | null;
  rfqStatus?: string | null;
  rfqMetadata?: Record<string, unknown> | null;
  hasQuotations?: boolean;
  hasFinalizedQuotation?: boolean;
  purchaseOrderId?: string | null;
  purchaseOrderStatus?: string | null;
  deliveryWorkflowStatus?: string | null;
  shipmentStatus?: string | null;
  hasActiveShipment?: boolean;
  hasActiveClaim?: boolean;
}

/**
 * Resolve the active workflow step from explicit API status or derived procurement state.
 * Never hardcodes a step — always driven by context.
 */
export function resolveProcurementWorkflowStatus(
  context: ProcurementWorkflowContext
): ProcurementWorkflowStatus {
  const explicit =
    normalizeProcurementWorkflowStatus(context.workflowStatus) ??
    normalizeProcurementWorkflowStatus(
      typeof context.rfqMetadata?.procurementWorkflowStatus === 'string'
        ? context.rfqMetadata.procurementWorkflowStatus
        : null
    );

  if (explicit && PROCUREMENT_WORKFLOW_STEPS.some(step => step.id === explicit)) {
    return explicit;
  }

  const rfqStatus = (context.rfqStatus || '').toLowerCase();
  const poStatus = (context.purchaseOrderStatus || '').toLowerCase();
  const deliveryWs = (context.deliveryWorkflowStatus || '').toLowerCase();
  const shipmentStatus = (context.shipmentStatus || '').toLowerCase();
  const shipmentInDelivery =
    context.hasActiveShipment || SHIPMENT_STATUSES_IN_DELIVERY.has(shipmentStatus);

  if (context.hasActiveClaim) {
    return 'CLAIM_MANAGEMENT';
  }

  const claimStepEligible =
    CLAIM_ELIGIBLE_DELIVERY_STATUSES.has(deliveryWs) ||
    (!deliveryWs && !shipmentInDelivery && ['delivered', 'completed'].includes(poStatus));

  if (claimStepEligible) {
    return 'CLAIM_MANAGEMENT';
  }

  if (
    poStatus === 'shipped' ||
    DELIVERY_WORKFLOW_STATUSES.has(deliveryWs) ||
    DELIVERY_PO_STATUSES.has(poStatus) ||
    rfqStatus === 'delivered' ||
    shipmentInDelivery
  ) {
    return 'DELIVERY';
  }

  if (context.purchaseOrderId || ORDERED_PO_STATUSES.has(poStatus)) {
    return 'ORDERED';
  }

  if (context.hasFinalizedQuotation) {
    return 'PURCHASE';
  }

  if (context.hasQuotations || rfqStatus === 'quoted') {
    return 'QUOTATION';
  }

  return 'RFQ';
}

export function getProcurementWorkflowStepIndex(status: ProcurementWorkflowStatus): number {
  return PROCUREMENT_WORKFLOW_STEPS.findIndex(step => step.id === status);
}

export function getStepVisualState(
  stepIndex: number,
  activeIndex: number
): StepVisualState {
  if (stepIndex < activeIndex) return 'completed';
  if (stepIndex === activeIndex) return 'active';
  return 'pending';
}

export function getWorkflowProgressPercent(activeIndex: number, totalSteps: number): number {
  if (totalSteps <= 1) return 0;
  return Math.min(100, Math.max(0, (activeIndex / (totalSteps - 1)) * 100));
}

export function getStepStatusLabel(state: StepVisualState): string {
  switch (state) {
    case 'completed':
      return 'Completed';
    case 'active':
      return 'Active';
    default:
      return 'Pending';
  }
}

export function getNextWorkflowStep(
  activeIndex: number
): ProcurementWorkflowStepDefinition | null {
  const nextIndex = activeIndex + 1;
  if (nextIndex >= PROCUREMENT_WORKFLOW_STEPS.length) return null;
  return PROCUREMENT_WORKFLOW_STEPS[nextIndex];
}

/** Business-friendly progress (step position out of total stages). */
export function getWorkflowStageProgressPercent(activeIndex: number, totalSteps: number): number {
  if (totalSteps <= 0) return 0;
  return Math.round(((activeIndex + 1) / totalSteps) * 100);
}

export function getActiveStepDefinition(
  status: ProcurementWorkflowStatus
): ProcurementWorkflowStepDefinition {
  return (
    PROCUREMENT_WORKFLOW_STEPS.find(step => step.id === status) ?? PROCUREMENT_WORKFLOW_STEPS[0]
  );
}
